import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import TicketBadge from '../components/TicketBadge';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, MapPin, Clock, ChevronRight, ChevronLeft, ChevronDown, Loader2, 
  Grid3X3, List, Tag, Building2, ArrowRight, Filter
} from 'lucide-react';
import SEO from '../components/SEO';
import { cityName, cityState } from '../utils/seoFormat';
import { breadcrumbLD, eventsItemListLD, collectionPageLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  party: 'Party',
  festival: 'Festival',
  brunch: 'Brunch',
  competition: 'Competition',
  tasting: 'Tasting',
  concert: 'Concert',
  networking: 'Networking',
  wine: 'Wine Events',
  other: 'Other'
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

export default function EventsListingPage() {
  const navigate = useNavigate();
  const { city = 'denver' } = useParams();
  const [events, setEvents] = useState([]);
  const [tags, setTags] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarEvents, setCalendarEvents] = useState([]);
  // Mobile-only collapsible: keep the Tag picker tucked away below the
  // listings by default so the actual events are the first thing visitors
  // (and Googlebot's mobile-first crawler) see.
  const [tagsOpenMobile, setTagsOpenMobile] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchTags();
    fetchVenues();
  }, [city, activeTab]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendarEvents();
    }
  }, [calendarYear, calendarMonth, activeTab]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'past' ? 'past' : activeTab === 'all' ? 'all' : 'upcoming';
      const response = await axios.get(`${API}/hosted-events?city=${city}&status=${status}&limit=50`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/hosted-events/tags/${city}`);
      setTags(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/hosted-events/venues/${city}`);
      setVenues(response.data || []);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      const response = await axios.get(`${API}/hosted-events/calendar/${city}?year=${calendarYear}&month=${calendarMonth}`);
      setCalendarEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getEventUrl = (event) => `/events/${event.city}/${event.slug}`;

  const prevMonth = () => {
    if (calendarMonth === 1) {
      setCalendarMonth(12);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const nextMonth = () => {
    if (calendarMonth === 12) {
      setCalendarMonth(1);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const firstDay = new Date(calendarYear, calendarMonth - 1, 1);
    const lastDay = new Date(calendarYear, calendarMonth, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    // Empty cells for days before first of month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Days of month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarEvents.filter(e => e.event_date === dateStr);
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      {(() => {
        const cityDisplay = cityName(city) || 'Denver';
        const state = cityState(city);
        const seoTitle = `${cityDisplay} Events Calendar — Parties, Festivals, Concerts & More`;
        const seoDescription = `Discover upcoming events in ${cityDisplay}${state ? ', ' + state : ''}. Parties, brunches, festivals, concerts and curated experiences from Eventism — secure tickets, instant digital wallet delivery.`;
        const seoKeywords = [
          `${cityDisplay} events`,
          `things to do in ${cityDisplay}`,
          `${cityDisplay} nightlife`,
          `${cityDisplay} parties`,
          `${cityDisplay} festivals`,
          `${cityDisplay} concerts`,
          `${cityDisplay} event calendar`,
          `${cityDisplay} this weekend`,
          state ? `${state} events` : '',
          'Eventism events',
        ].filter(Boolean);
        const items = eventsItemListLD(
          events || [],
          `${cityDisplay} Events`,
          `/events/${city}`,
        );
        return (
          <SEO
            title={seoTitle}
            description={seoDescription}
            keywords={seoKeywords}
            structuredData={[
              collectionPageLD(seoTitle, seoDescription),
              breadcrumbLD([
                { name: 'Home', url: '/' },
                { name: 'Events', url: '/events' },
                { name: cityDisplay, url: `/events/${city}` },
              ]),
              items,
            ].filter(Boolean)}
          />
        );
      })()}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">Events</span>
          </nav>
          <h1 className="text-4xl font-bold mb-2">Eventism Events</h1>
          <p className="text-muted-foreground text-lg">
            Discover our upcoming parties, festivals, and experiences in {city.charAt(0).toUpperCase() + city.slice(1)}
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar — `order-last lg:order-first` pushes Tags & Venues BELOW
              the main event list on mobile (Googlebot mobile-first sees
              events first) and keeps the familiar left-rail layout on
              desktop. */}
          <div className="lg:col-span-1 space-y-6 order-last lg:order-first">
            {/* Tags */}
            {tags.length > 0 && (
              <Card data-testid="browse-by-tag-card">
                <CardHeader className="pb-3">
                  <button
                    type="button"
                    onClick={() => setTagsOpenMobile(v => !v)}
                    className="w-full flex items-center justify-between lg:cursor-default lg:pointer-events-none"
                    aria-expanded={tagsOpenMobile}
                    aria-controls="browse-by-tag-content"
                    data-testid="browse-by-tag-toggle"
                  >
                    <CardTitle className="text-base flex items-center gap-2">
                      <Tag className="h-4 w-4" /> Browse by Tag
                      <span className="ml-1 text-xs font-normal text-muted-foreground">({tags.length})</span>
                    </CardTitle>
                    {/* Chevron only visible / functional on mobile */}
                    <ChevronDown
                      className={`h-4 w-4 lg:hidden transition-transform ${tagsOpenMobile ? 'rotate-180' : ''}`}
                    />
                  </button>
                </CardHeader>
                <CardContent
                  id="browse-by-tag-content"
                  className={`flex flex-wrap gap-2 ${tagsOpenMobile ? 'block' : 'hidden'} lg:flex`}
                >
                  {tags.map(t => (
                    <Link key={t.tag} to={`/events/${city}/tags/${t.tag}`}>
                      <Badge variant="outline" className="cursor-pointer hover:bg-muted">
                        {t.tag} ({t.count})
                      </Badge>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Venues */}
            {venues.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-4 w-4" /> Venues
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {venues.slice(0, 10).map(v => (
                    <Link 
                      key={v.slug} 
                      to={`/${city}/venues/${v.slug}`}
                      className="block text-sm hover:text-indigo-500 transition-colors"
                    >
                      {v.name} <span className="text-muted-foreground">({v.event_count})</span>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content — render first on mobile (order-first), preserves
              left-rail layout on desktop. */}
          <div className="lg:col-span-3 order-first lg:order-none">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between mb-6">
                <TabsList>
                  <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                  <TabsTrigger value="calendar">Calendar</TabsTrigger>
                  <TabsTrigger value="past">Past</TabsTrigger>
                  <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
                {activeTab !== 'calendar' && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid3X3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <TabsContent value="upcoming" className="mt-0">
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" />
                  </div>
                ) : events.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No upcoming events at the moment</p>
                      <p className="text-sm text-muted-foreground mt-2">Check back soon!</p>
                    </CardContent>
                  </Card>
                ) : viewMode === 'grid' ? (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {events.map(event => (
                      <EventCard key={event.event_id} event={event} onClick={() => navigate(getEventUrl(event))} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {events.map(event => (
                      <EventListItem key={event.event_id} event={event} onClick={() => navigate(getEventUrl(event))} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="calendar" className="mt-0">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Button variant="ghost" size="icon" onClick={prevMonth}>
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <h3 className="text-lg font-semibold">
                        {MONTHS[calendarMonth - 1]} {calendarYear}
                      </h3>
                      <Button variant="ghost" size="icon" onClick={nextMonth}>
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-7 gap-1 text-center mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                        <div key={d} className="text-xs font-medium text-muted-foreground py-2">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {generateCalendarDays().map((day, idx) => {
                        const dayEvents = getEventsForDay(day);
                        return (
                          <div 
                            key={idx} 
                            className={`min-h-[80px] p-1 border rounded ${day ? 'bg-background' : 'bg-muted/30'}`}
                          >
                            {day && (
                              <>
                                <span className="text-sm font-medium">{day}</span>
                                <div className="space-y-1 mt-1">
                                  {dayEvents.slice(0, 2).map(e => (
                                    <Link
                                      key={e.event_id}
                                      to={getEventUrl(e)}
                                      className="block text-xs p-1 bg-indigo-500 text-foreground rounded truncate hover:bg-indigo-600"
                                    >
                                      {e.name}
                                    </Link>
                                  ))}
                                  {dayEvents.length > 2 && (
                                    <span className="text-xs text-muted-foreground">+{dayEvents.length - 2} more</span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="past" className="mt-0">
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" />
                  </div>
                ) : events.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <p className="text-muted-foreground">No past events</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {events.map(event => (
                      <EventCard key={event.event_id} event={event} onClick={() => navigate(getEventUrl(event))} isPast />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="all" className="mt-0">
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" />
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {events.map(event => (
                      <EventCard key={event.event_id} event={event} onClick={() => navigate(getEventUrl(event))} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

// Event Card Component
function EventCard({ event, onClick, isPast = false }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <Card 
      className={`overflow-hidden cursor-pointer hover:shadow-lg transition-shadow ${isPast ? 'opacity-75' : ''}`}
      onClick={onClick}
      data-testid={`event-card-${event.slug}`}
    >
      {event.image_url && (
        <div className="h-48 overflow-hidden relative">
          <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
          {event.is_featured && (
            <Badge className="absolute top-3 right-3 bg-amber-500">Featured</Badge>
          )}
          {event.badge && (
            <div className="absolute top-3 left-3">
              <TicketBadge badge={event.badge} size="sm" testId={`event-card-badge-${event.event_id}`} />
            </div>
          )}
          {isPast && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <Badge variant="secondary">Past Event</Badge>
            </div>
          )}
        </div>
      )}
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline">{CATEGORY_LABELS[event.category] || event.category}</Badge>
          {event.age_restriction && (
            <Badge variant="secondary" className="text-xs">{event.age_restriction}</Badge>
          )}
        </div>
        <h3 className="font-semibold text-lg mb-1">{event.name}</h3>
        {event.tagline && (
          <p className="text-sm text-muted-foreground mb-2">{event.tagline}</p>
        )}
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {formatDate(event.event_date)}
          </span>
          {event.venue_name && (
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {event.venue_name}
            </span>
          )}
        </div>
        {event.ticket_price_min && (
          <p className="text-indigo-500 font-semibold mt-3">
            From ${event.ticket_price_min}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Event List Item Component
function EventListItem({ event, onClick }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <Card className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={onClick}>
      <div className="flex">
        {event.image_url && (
          <div className="w-48 h-32 flex-shrink-0">
            <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 p-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[event.category]}</Badge>
            </div>
            <h3 className="font-semibold text-lg">{event.name}</h3>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(event.event_date)}
              </span>
              {event.venue_name && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {event.venue_name}
                </span>
              )}
            </div>
          </div>
          {event.ticket_price_min && (
            <div className="text-right">
              <p className="text-indigo-500 font-semibold">From ${event.ticket_price_min}</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
